import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '../lib/api';

interface OfferLite {
  id: number;
  name: string;
  networkName: string | null;
}

interface PublisherLite {
  id: number;
  subId: string;
  displayName: string | null;
}

interface CampaignLite {
  id: number;
  slug: string;
  displayName: string;
}

/**
 * Fetch all offers once and provide a stable id → name lookup.
 * Cached 5 minutes — small dataset (rarely >50 rows) so we trade staleness for
 * cheap per-row lookups across multiple pages (Conversions, Clicks, Statistics).
 */
export function useOfferLookup() {
  const q = useQuery({
    queryKey: ['offers-lookup'],
    // BUGFIX: was `/api/v1/offers` which is admin-only (requireAdmin). When
    // a publisher (user role) viewed /clicks or /conversions, the request
    // 403'd, q.data was empty, and the Offer column fell back to "#<id>"
    // instead of the offer name. Now uses the public lookup endpoint that
    // returns minimal display data (id, name, networkName) for both roles.
    queryFn: () => apiClient.get<OfferLite[]>('/api/v1/lookups/offers'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Build a Map once per response. useMemo prevents recreating on every render.
  const map = useMemo(() => {
    const m = new Map<number, OfferLite>();
    for (const o of q.data ?? []) m.set(o.id, o);
    return m;
  }, [q.data]);

  // D9: wrap in useMemo so consumers get a stable object reference each render.
  // Without this, every parent re-render produces a new object and any child that
  // spreads these helpers into a useMemo dep array re-runs unnecessarily.
  return useMemo(
    () => ({
      /** Returns the offer name, or "—" if not found (covers null/missing offer_id). */
      getName: (id: number | null | undefined): string => {
        if (id == null) return '—';
        return map.get(id)?.name ?? `#${id}`;
      },
      /** Returns "{networkName} · {offerName}" — useful in tight columns. */
      getLabel: (id: number | null | undefined): string => {
        if (id == null) return '—';
        const o = map.get(id);
        if (!o) return `#${id}`;
        return o.networkName ? `${o.networkName} · ${o.name}` : o.name;
      },
      map,
      isLoading: q.isLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, q.isLoading],
  );
}

/**
 * Same pattern for campaigns. Clicks/AE returns `campaign_slug` (not id), so we
 * also expose a slug→displayName map.
 */
export function useCampaignLookup() {
  const q = useQuery({
    queryKey: ['campaigns-lookup'],
    // BUGFIX: was `/api/v1/campaigns` which is admin-only. Same issue as
    // useOfferLookup — publishers saw raw slug "rj" instead of display name
    // "RJ Network" in the Campaign column. Public lookup endpoint serves
    // both roles with minimal data.
    queryFn: () => apiClient.get<CampaignLite[]>('/api/v1/lookups/campaigns'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const bySlug = useMemo(() => {
    const m = new Map<string, CampaignLite>();
    for (const c of q.data ?? []) m.set(c.slug, c);
    return m;
  }, [q.data]);

  const byId = useMemo(() => {
    const m = new Map<number, CampaignLite>();
    for (const c of q.data ?? []) m.set(c.id, c);
    return m;
  }, [q.data]);

  // D9: stable reference
  return useMemo(
    () => ({
      /** Resolve display name from slug — Clicks/AE only has slug. */
      nameFromSlug: (slug: string | null | undefined): string => {
        if (!slug) return '—';
        return bySlug.get(slug)?.displayName ?? slug;
      },
      nameFromId: (id: number | null | undefined): string => {
        if (id == null) return '—';
        return byId.get(id)?.displayName ?? `#${id}`;
      },
      bySlug,
      byId,
      isLoading: q.isLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bySlug, byId, q.isLoading],
  );
}

/** Same pattern for publishers — used by Overviews to swap "Publisher ID" → name. */
export function usePublisherLookup() {
  const q = useQuery({
    queryKey: ['publishers-lookup'],
    // BUGFIX: was /api/v1/publishers (admin-only). User role saw "#4, #18"
    // in Statistics/Overviews Publisher column because the fetch 403'd.
    // Public lookup endpoint returns minimal display fields for both roles.
    queryFn: () => apiClient.get<PublisherLite[]>('/api/v1/lookups/publishers'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const map = useMemo(() => {
    const m = new Map<number, PublisherLite>();
    for (const p of q.data ?? []) m.set(p.id, p);
    return m;
  }, [q.data]);

  // D9: stable reference
  return useMemo(
    () => ({
      /** Returns displayName, or subId, or "#id", or "—". */
      getName: (id: number | null | undefined): string => {
        if (id == null) return '—';
        const p = map.get(id);
        if (!p) return `#${id}`;
        return p.displayName || p.subId || `#${id}`;
      },
      /** Returns the sub_id slug specifically (for monospace pill style). */
      getSubId: (id: number | null | undefined): string => {
        if (id == null) return '—';
        return map.get(id)?.subId ?? `#${id}`;
      },
      map,
      isLoading: q.isLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, q.isLoading],
  );
}
